/**
 * One definition of "location order" for the whole app.
 *
 * Every list of locations a user reads — the SVG import preview, the settings
 * tree, the map plan tabs, the location pickers and filters — sorts through the
 * comparators in this file, so a room cannot appear in one order on one screen
 * and another order on the next.
 *
 * Why not `String.prototype.localeCompare` (which two call sites used before):
 * room codes are alphanumeric, and a plain comparison is character-wise, so
 * `A.10` sorts BEFORE `A.9` — the digits are compared as text, `1` < `9`. An
 * `Intl.Collator` with `numeric: true` compares runs of digits as numbers, which
 * is what a human means by "A to Z".
 *
 * The collator is built ONCE at module scope. Constructing one is expensive
 * (it resolves locale data), and these lists run to hundreds of rows, so a
 * per-comparison collator would pay that cost O(n log n) times.
 *
 * The locale is deliberately left to the runtime (`undefined`), which resolves to
 * the OS/browser locale — NOT to the language picked in the app's own switcher
 * (`NavLangPopover.tsx` → `i18n.changeLanguage`). The two can disagree: on an
 * en-US system, `Ł` sorts after `Z` even with Polish selected in-app. That is the
 * pre-existing behaviour of the `localeCompare()` calls this file replaced
 * (measured identical), so no screen got worse — but do not read `undefined` as a
 * promise of display-language collation. Pass `i18n.language` explicitly if that
 * is ever wanted.
 */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

/**
 * The fields a location-shaped record can be ordered by.
 *
 * `@id`/`id` are ordering keys of last resort, never displayed. They exist so the
 * comparison can always separate two structurally distinct rows — see
 * `compareLocations`.
 */
export type LocationOrderKeys = {
  /**
   * The user's own chosen order, when there is one. `null`/`undefined` means
   * "never reordered" — see `comparePosition`.
   */
  position?: number | null
  name?: string | null
  code?: string | null
  '@id'?: string | null
  id?: string | number | null
}

/**
 * Natural, case- and accent-insensitive comparison of two display strings.
 *
 * Blank input (`null`, `undefined`, whitespace) is not an error and does not
 * throw: it sorts LAST, so a half-filled row sinks to the bottom of the list
 * instead of heading it. Two blanks compare equal.
 */
export function compareNaturalText(a?: string | null, b?: string | null): number {
  const left = typeof a === 'string' ? a.trim() : ''
  const right = typeof b === 'string' ? b.trim() : ''

  if (left === '' || right === '') {
    if (left === right) return 0
    return left === '' ? 1 : -1
  }

  return collator.compare(left, right)
}

/**
 * Order two locations by the order the user CHOSE, if they chose one; otherwise by
 * what they actually read — the name — then by code, then by identity.
 *
 * THE THIRD KEY IS WHY THIS IS A TOTAL ORDER, and the reason it exists is exactly
 * the shape the reported building uses. Numeric collation compares digit runs as
 * NUMBERS, so `A.015` and `A.15` are the same number: measured,
 * `collator.compare('A.15', 'A.015')` is 0 in BOTH directions. A row whose name
 * AND code both tie that way — the normal case, since rooms are usually named
 * after their code — would then be ordered by nothing at all. `Array.sort` is
 * stable, so those two rows would silently keep whatever order the API returned
 * them in, which is the unspecified Postgres row order this whole change exists
 * to stop depending on.
 *
 * Identity is compared as PLAIN text, deliberately not through the collator: the
 * collator is the thing that produces ties, so running the last-resort key
 * through it would reintroduce the gap it is here to close.
 */
export function compareLocations(a: LocationOrderKeys, b: LocationOrderKeys): number {
  return (
    comparePosition(a?.position, b?.position) ||
    compareNaturalText(a?.name, b?.name) ||
    compareNaturalText(a?.code, b?.code) ||
    compareIdentity(a, b)
  )
}

/**
 * The user's manual order, when they have set one.
 *
 * ABSENT IS NOT ZERO, and that is the entire point. A location the user has never
 * dragged carries no position, and must sort AFTER every location that has one —
 * so a freshly imported room lands at the end of a hand-ordered list instead of
 * jumping to its head. Treating absence as `0` would do exactly the opposite.
 *
 * This mirrors what the server does, on purpose: PostgreSQL sorts NULLs LAST on an
 * ASC sort, so `ORDER BY position ASC, code ASC, id ASC` and this comparator agree
 * row for row. They have to — the settings list renders the API's order directly
 * while the map editor sorts client-side, and a disagreement would show up as a
 * list that reshuffles when it is saved.
 *
 * Two rows with the same position fall through to name/code/identity rather than
 * being declared equal, so a partially numbered list still has a total order.
 */
function comparePosition(a?: number | null, b?: number | null): number {
  const left = typeof a === 'number' && Number.isFinite(a) ? a : null
  const right = typeof b === 'number' && Number.isFinite(b) ? b : null

  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1

  return left - right
}

/** Last-resort ordering key: stable, never displayed, never collated. */
function compareIdentity(a: LocationOrderKeys, b: LocationOrderKeys): number {
  const left = identityOf(a)
  const right = identityOf(b)

  if (left === right) return 0
  if (left === '') return 1
  if (right === '') return -1

  return left < right ? -1 : 1
}

function identityOf(location: LocationOrderKeys): string {
  const iri = location?.['@id']
  if (typeof iri === 'string' && iri.trim() !== '') return iri.trim()

  const id = location?.id
  if (typeof id === 'string' || typeof id === 'number') return String(id).trim()

  return ''
}

/**
 * A sorted COPY of a location list — the input array is never reordered in
 * place, because callers hand us React state and form values that must not
 * mutate under their owner.
 *
 * A missing list yields an empty array rather than throwing, so a caller can
 * pass a query result that has not resolved yet.
 */
export function sortLocations<T extends LocationOrderKeys>(
  locations: readonly T[] | null | undefined,
): T[] {
  if (!Array.isArray(locations)) return []
  return [...locations].sort(compareLocations)
}
